import React, { useEffect, useState } from "react";
import { BsFillCaretRightFill } from "react-icons/bs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SuccessAlert from "../../../components/alerts/SuccessAlert";
import ErrorAlert from "../../../components/alerts/ErrorAlert";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import MainLoader from "../../../components/loading/MainLoader";
import {
  fetchCategories,
  getFormDetailsByProduct,
  submitFormDetails,
} from "../../../store/actions/fault.action";
import AlertModal from "../../../components/alertModal/alertModal";
import ServiceRequestSuccessMessage from "./ServiceRequestSuccess/ServiceRequestSuccess";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

interface FormField {
  id: number;
  formName: string;
  displayFieldName: string;
  fieldType: string;
  value: string;
  isRequired: boolean | null;
  fieldOrder: number;
  fieldName: string;
}

export interface Product {
  id: number;
  displayName: string;
  name: string;
}

interface Category {
  category: string;
  products: Product[];
}

interface ApiResponse {
  dataBundle: Category[];
}

// Each entry is a group of keywords that must ALL appear in the form name for a
// match. Add a new group here to enable Service Location Identifier for a form.
const SERVICE_LOCATION_FORM_KEYWORDS: string[][] = [
  ["ip", "end"], // IP EndPoint Service Request Form
  ["ip", "trunk"], // IPTrunk Line Request Form
  ["business", "internet"], // Business Internet Line Request Form
  ["enet", "access"], // E Net Access Request Form
  ["premium", "ipvpn"], // Premium IPVPN Request Form
  ["play"], // SLT Fiber Line: Single/Double/Triple Play Request Forms
  ["azure", "stack"], // Cloud Solutions: Azure Stack Service Request Form
  ["data", "one"], // Managed Services: Data One Request Form
];

const SERVICE_ADDRESS_HELPER =
  "Enter the full service address";
const SERVICE_LOCATION_IDENTIFIER_HELPER =
  "Enter a location id for reference (max 20)";
const SERVICE_LOCATION_IDENTIFIER_MAX_LENGTH = 20;

const requiresServiceLocationIdentifier = (product: Product): boolean => {
  const name = `${product.displayName} ${product.name}`
    .toLowerCase()
    .replace(/\s/g, "");
  return SERVICE_LOCATION_FORM_KEYWORDS.some((keywords) =>
    keywords.every((keyword) => name.includes(keyword))
  );
};

const isServiceAddressField = (field: FormField): boolean => {
  const normalizedDisplay = field.displayFieldName
    .toLowerCase()
    .replace(/\s+/g, "");
  const normalizedField = field.fieldName.toLowerCase().replace(/_/g, "");

  return (
    (normalizedDisplay.includes("service") &&
      (normalizedDisplay.includes("address") ||
        normalizedDisplay.includes("adderess"))) ||
    (normalizedField.includes("service") &&
      (normalizedField.includes("address") ||
        normalizedField.includes("adderess"))) ||
    normalizedDisplay === "installationaddress" ||
    normalizedField === "installationaddress"
  );
};

const isServiceLocationIdentifierField = (field: FormField): boolean =>
  field.displayFieldName === "Service Location Identifier" ||
  field.fieldName === "Service_Location_Identifier";

const getFieldPlaceholder = (field: FormField): string => {
  if (isServiceAddressField(field)) {
    return SERVICE_ADDRESS_HELPER;
  }
  if (isServiceLocationIdentifierField(field)) {
    return SERVICE_LOCATION_IDENTIFIER_HELPER;
  }
  return `Enter ${field.displayFieldName}`;
};

const normalizeServiceAddressFields = (fields: FormField[]): FormField[] =>
  fields.map((field) =>
    isServiceAddressField(field)
      ? {
          ...field,
          displayFieldName: "Service Address",
          fieldName: "Service_Address",
        }
      : field
  );

const withServiceAddressField = (
  fields: FormField[],
  product: Product
): FormField[] => {
  if (
    !requiresServiceLocationIdentifier(product) ||
    fields.some(isServiceAddressField)
  ) {
    return fields;
  }

  const maxOrder = fields.reduce(
    (max, field) => Math.max(max, field.fieldOrder ?? 0),
    0
  );

  return [
    ...fields,
    {
      id: -2,
      formName: product.name,
      displayFieldName: "Service Address",
      fieldType: "Text",
      value: "",
      isRequired: true,
      fieldOrder: maxOrder + 1,
      fieldName: "Service_Address",
    },
  ];
};

const buildSubmissionPayload = (
  formValues: Record<string, string>,
  formFields: FormField[]
): Record<string, string> => {
  const payload = { ...formValues };
  const addressField = formFields.find(isServiceAddressField);

  if (addressField) {
    payload.Service_Address =
      formValues[addressField.fieldName] || payload.Service_Address || "";
    if (addressField.fieldName !== "Service_Address") {
      delete payload[addressField.fieldName];
    }
  }

  return payload;
};

const withServiceLocationIdentifierField = (
  fields: FormField[],
  product: Product
): FormField[] => {
  if (!requiresServiceLocationIdentifier(product)) {
    return fields;
  }

  const hasLocationField = fields.some(
    (field) => field.displayFieldName === "Service Location Identifier"
  );
  if (hasLocationField) {
    // Keep existing DB field, but always treat it as optional.
    return fields.map((field) =>
      field.displayFieldName === "Service Location Identifier"
        ? { ...field, isRequired: false }
        : field
    );
  }

  const maxOrder = fields.reduce(
    (max, field) => Math.max(max, field.fieldOrder ?? 0),
    0
  );

  return [
    ...fields,
    {
      id: -1,
      formName: product.name,
      displayFieldName: "Service Location Identifier",
      fieldType: "Text",
      value: "",
      isRequired: false,
      fieldOrder: maxOrder + 1,
      fieldName: "Service_Location_Identifier",
    },
  ];
};

const ServiceRequest = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categoryList, setCategoryList] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [referenceNumber, SetReferenceNumber] = useState("");

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const dataBundle = await fetchCategories();
        setCategories(dataBundle);

        const catList = dataBundle.map((c: any) => c.category);
        setSelectedCategory(catList[0]);
        setCategoryList(catList);
      } catch (err) {
        setError("Failed to fetch service categories");
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, []);

  const handleProductClick = async (product: Product) => {
    try {
      const dataBundle = withServiceLocationIdentifierField(
        withServiceAddressField(
          normalizeServiceAddressFields(await getFormDetailsByProduct(product)),
          product
        ),
        product
      );

      setFormFields(dataBundle);
      setSelectedProduct(product);

      const initialValues: Record<string, string> = {};
      dataBundle.forEach((field: FormField) => {
        initialValues[field.fieldName] = "";
      });

      setFormValues(initialValues);
      setFieldErrors({});
    } catch (err) {
      setError("Failed to load service form");
    }
  };

  const validateField = (field: FormField, value: string): string | null => {
    if (field.isRequired && (!value || value.trim() === "")) {
      return `${field.displayFieldName} is required`;
    }
    if (
      isServiceLocationIdentifierField(field) &&
      value &&
      value.length > SERVICE_LOCATION_IDENTIFIER_MAX_LENGTH
    ) {
      return `Service Location Identifier must be maximum ${SERVICE_LOCATION_IDENTIFIER_MAX_LENGTH} characters`;
    }
    if (field.fieldType === "Calender" && isNaN(Date.parse(value))) {
      return "Invalid date format";
    }
    return null;
  };

  const handleInputChange = (fieldName: string, value: string | Date) => {
    const field = formFields.find((f) => f.fieldName === fieldName);
    let stringValue = value instanceof Date ? value.toISOString() : value;

    if (
      field &&
      isServiceLocationIdentifierField(field) &&
      typeof stringValue === "string"
    ) {
      stringValue = stringValue.slice(0, SERVICE_LOCATION_IDENTIFIER_MAX_LENGTH);
    }

    if (field) {
      const error = validateField(field, stringValue);
      setFieldErrors((prev) => ({
        ...prev,
        [fieldName]: error || "",
      }));
    }

    setFormValues((prev) => ({
      ...prev,
      [fieldName]: stringValue,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Validate all fields
    const errors: Record<string, string> = {};
    formFields.forEach((field) => {
      const error = validateField(field, formValues[field.fieldName] || "");
      if (error) {
        errors[field.fieldName] = error;
      }
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSubmitting(false);
      return;
    }

    try {
      const response = await submitFormDetails(
        buildSubmissionPayload(formValues, formFields),
        selectedProduct?.displayName || ""
      );

      if (response.isSuccess) {
        setSuccess(true);

        setSelectedProduct(null);
        setFormFields([]);
        setFormValues({});
        setFieldErrors({});
        SetReferenceNumber(response?.crmResponse?.result);
      } else {
        setError(response.errorMessage || "Failed to submit form");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <MainLoader show={isLoading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/serviceRequest", title: "New Service Request" },
        ]}
      />

      <div className="font-popins dataAddOns common-panel">
        {success ? (
          <ServiceRequestSuccessMessage
            handleClose={() => setSuccess(false)}
            referenceNo={referenceNumber}
            faultType={"service"}
          />
        ) : (
          <>
            {error && (
              // <ErrorAlert
              //   error={error}
              //   setError={setError}
              //   setIsError={() => setError(null)}
              // />
              <AlertModal
                show={error !== null}
                variant="error"
                buttons={[
                  {
                    text: "Ok",
                    onClick: () => {
                      setError(null);
                    },
                    variant: "danger",
                  },
                ]}
              >
                {error}
              </AlertModal>
            )}

            {!selectedProduct ? (
              <div className="addon-container">
                <div className="leftSide">
                  {categoryList.map((category, index) => (
                    <div
                      key={index}
                      className="buttonTab-wrapper"
                      hidden={
                        !categories.find((c) => c.category === category)
                          ?.products.length
                      }
                    >
                      <div
                        className={`buttonTab ${
                          selectedCategory === category ? "blue" : ""
                        }`}
                        onClick={() => setSelectedCategory(category)}
                      >
                        <div className="text-center">
                          <p className="m-2">{category}</p>
                        </div>
                        {selectedCategory === category && (
                          <BsFillCaretRightFill size={20} color="#007ed6" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="rightSideV2 service-selection-panel">
                  <div className="service-grid">
                    {categories
                      .find((c) => c.category === selectedCategory)
                      ?.products.map((product, index) => (
                        <div
                          key={index}
                          className="service-card"
                          onClick={() => handleProductClick(product)}
                        >
                          <div className="service-card-inner">
                            <div className="service-title">
                              <span className="service-icon">📦</span>
                              {product.displayName}
                            </div>
                            <div className="service-hover-content">
                              <span className="select-indicator">
                                Select Service →
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="service-form font-popins">
                <h2 className="form-title">
                  {selectedProduct?.displayName} Request Form
                </h2>
                <form onSubmit={handleSubmit} className="enterprise-form">
                  <div className="form-grid">
                    {formFields
                      .sort((a, b) => a.fieldOrder - b.fieldOrder)
                      .map((field) => (
                        <div
                          key={field.id}
                          className={`form-field ${field.fieldType.toLowerCase()} ${
                            fieldErrors[field.fieldName] ? "invalid" : ""
                          }`}
                        >
                          <label className="form-label font-popins">
                            {field.displayFieldName}
                            {field.isRequired && (
                              <span className="required">*</span>
                            )}
                          </label>

                          {field.fieldType === "DropDown" ? (
                            // <div className="select-wrapper">
                            //   <select
                            //     className="form-input"
                            //     value={formValues[field.fieldName]}
                            //     onChange={(e) =>
                            //       handleInputChange(
                            //         field.fieldName,
                            //         e.target.value
                            //       )
                            //     }
                            //     required={field.isRequired || false}
                            //   >
                            //     <option value="">
                            //       Select {field.displayFieldName}
                            //     </option>
                            //     {field.value.split(",").map((option, index) => (
                            //       <option key={index} value={option.trim()}>
                            //         {option.trim()}
                            //       </option>
                            //     ))}
                            //   </select>
                            // </div>
                            <div className="select-wrapper">
                              <Select
                                value={formValues[field.fieldName]}
                                onChange={(e) =>
                                  handleInputChange(
                                    field.fieldName,
                                    e.target.value
                                  )
                                }
                                required={field.isRequired || false}
                                displayEmpty
                                sx={{
                                  width: "100%",
                                  height: "48px",
                                  fontSize: "0.9rem",
                                  "& .MuiSelect-select": {
                                    color: formValues[field.fieldName]
                                      ? "inherit"
                                      : "gray",
                                  },
                                }}
                                MenuProps={{
                                  anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "left",
                                  },
                                  transformOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                  },
                                  PaperProps: {
                                    sx: {
                                      "& .MuiMenuItem-root": {
                                        color: "gray",
                                        fontStyle: "normal",

                                        "&:hover": {
                                          backgroundColor: "#f5f5f5",
                                        },
                                      },
                                    },
                                  },
                                }}
                                renderValue={(selected) => {
                                  if (!selected) {
                                    return (
                                      <span style={{ color: "gray" }}>
                                        Select {field.displayFieldName}
                                      </span>
                                    );
                                  }
                                  return selected;
                                }}
                              >
                                <MenuItem value="" sx={{ display: "none" }}>
                                  {" "}
                                  Select {field.displayFieldName}
                                </MenuItem>
                                {field.value.split(",").map((option, index) => (
                                  <MenuItem key={index} value={option.trim()}>
                                    {option.trim()}
                                  </MenuItem>
                                ))}
                              </Select>
                            </div>
                          ) : field.fieldType === "Calender" ? (
                            <div className="datepicker-wrapper">
                              <DatePicker
                                selected={
                                  formValues[field.fieldName]
                                    ? new Date(formValues[field.fieldName])
                                    : null
                                }
                                onChange={(date: Date) =>
                                  handleInputChange(field.fieldName, date)
                                }
                                dateFormat="dd/MM/yyyy"
                                className="form-input"
                                placeholderText="Select Date"
                                required={field.isRequired || false}
                                minDate={
                                  new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
                                }
                                filterDate={(date) => {
                                  return (
                                    date >
                                    new Date(
                                      Date.now() + 1 * 24 * 60 * 60 * 1000
                                    )
                                  );
                                }}
                              />
                            </div>
                          ) : (
                            <input
                              type="text"
                              className="form-input"
                              value={formValues[field.fieldName] || ""}
                              onChange={(e) =>
                                handleInputChange(
                                  field.fieldName,
                                  e.target.value
                                )
                              }
                              required={field.isRequired || false}
                              maxLength={
                                isServiceLocationIdentifierField(field)
                                  ? SERVICE_LOCATION_IDENTIFIER_MAX_LENGTH
                                  : undefined
                              }
                              placeholder={getFieldPlaceholder(field)}
                            />
                          )}
                          {fieldErrors[field.fieldName] && (
                            <div className="error-message">
                              {fieldErrors[field.fieldName]}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setSelectedProduct(null)}
                      disabled={submitting}
                    >
                      ← Back
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit Request"}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceRequest;
